<template>
  <DsPageContent>
    <DsHero
      v-if="content.title"
      :title="content.title"
      :text="content.description"
      theme="grey"
    >
      <template
        v-if="icon"
        #eyebrow
      >
        <DsIcon
          :category="icon.category"
          :name="icon.name"
          class="ds-margin-bottom-24"
        />
      </template>
    </DsHero>
    <DsSectionImage
      v-if="content.headingMedia || content.headingVideo"
      theme-variant="grey-light"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsScreenWrapper
            v-if="content.headingMedia"
            :ready="hydrateReady"
          >
            <DsImageElement
              v-if="content.headingMedia"
              :image="content.headingMedia"
              :screen="screen"
            />
          </DsScreenWrapper>
          <DsScreenWrapper
            v-if="content.headingVideo"
            :ready="hydrateReady"
          >
            <ClientOnly>
              <DsVideoPlayer
                v-if="content.headingVideo"
                :aria-label-play="labels.play"
                :aria-label-close="labels.close"
                :screen="screen"
                v-bind="content.headingVideo"
                class="ds-width-full"
              />
            </ClientOnly>
          </DsScreenWrapper>
        </div>
      </div>
    </DsSectionImage>
    <DsSection
      :space-options-medium="{ top: false, bottom: true }"
      class="ds-md-padding-top-48"
    >
      <div class="ds-grid">
        <h2
          class="ds-heading ds-grid__col ds-grid__col--12"
          v-text="content.headingTitle"
        />
        <RichText
          class="ds-margin-top-24 ds-grid__col ds-grid__col--12 ds-md-grid__col--7 ds-text"
          :data="content.headingDescription"
        />
      </div>
    </DsSection>
    <!-- Heading Testimonials -->
    <DsSection
      v-if="content.headingTestimonials"
      :space-options-medium="{ top: false, bottom: true }"
      :space-options-small="{ top: false, bottom: true }"
    >
      <DsGrid
        :items="content.headingTestimonials?.items"
        :column-width="{ small: 12, medium: 6, large: 6 }"
      >
        <template #default="{ item }">
          <DsCard
            :image="item.image"
            :video="appendLabelsToVideo(item.video)"
            :title="item.title"
            :aria-label-play="labels.play"
            :aria-label-close="labels.close"
            aspect-ratio="16/9"
          >
            <template #text>
              <p
                class="ds-text ds-margin-top-12"
                v-text="item.description"
              />
            </template>
          </DsCard>
        </template>
      </DsGrid>
    </DsSection>
    <!-- Testimonials -->
    <DsSection
      v-if="content.testimonials"
      theme="grey"
      full-screen
    >
      <ClientOnly>
        <DsImageGallery
          v-if="content.testimonials"
          :screen="screen"
          :items="content.testimonials.items?.length"
          :title="content.testimonials.title"
          :description="content.testimonials.description"
          :width="{ small: 272, medium: 368, large: 368 }"
        >
          <template #default="{ index }">
            <DsCard
              :image="testimonialForIndex(index).image"
              :video="appendLabelsToVideo(testimonialForIndex(index).video)"
              :title="testimonialForIndex(index).author || testimonialForIndex(index).title"
              :subtitle="testimonialForIndex(index).role"
              aspect-ratio="16/9"
              class="ds-border-radius ds-height-full"
            >
              <template #text>
                <p
                  class="ds-text ds-margin-top-12"
                  v-text="testimonialForIndex(index).quote || testimonialForIndex(index).description"
                />
              </template>
            </DsCard>
          </template>
        </DsImageGallery>
      </ClientOnly>
    </DsSection>
    <DsSection
      v-if="content.logoBar"
      theme="grey"
      :with-space="false"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsDivider />
        </div>
      </div>
    </DsSection>
    <!-- Logobar -->
    <DsSection
      v-if="content.logoBar"
      theme="grey"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsLogoBar
            v-if="content.logoBar"
            :title="content.logoBar.title"
            :items="content.logoBar.items"
            align-center
          />
        </div>
      </div>
    </DsSection>
    <!-- CareersVacanciesList -->
    <DsSection>
      <div class="ds-grid">
        <div class="ds-grid__col">
          <CareersVacanciesList
            with-limit
            sort-by-latest
            as-widget
          />
        </div>
      </div>
    </DsSection>
  </DsPageContent>
</template>
<script setup>
import {
  DsPageContent,
  DsHero,
  DsIcon,
  DsSectionImage,
  DsScreenWrapper,
  DsVideoPlayer,
  DsImageElement,
  DsSection,
  DsLogoBar,
  DsDivider,
  DsCard,
  DsImageGallery,
  DsGrid
} from 'awds'
import { queryPageCareersCareerTypesDetail } from '../../data-layer/queries'
import mapperIcon from '../../../core/data-layer/utils/mappers/mapper-icon'
import { getIconForCareerType } from '../../data-layer/greenhouse/mappers-greenhouse'

const { locale } = useI18n()
const { screen } = useScreen()
const { useAsyncDataWithQuery, onBeforeAdyenData, onAfterAdyenData, content } = useCareersPageData()
const { labels } = useCareersGlobalData()
const slug = useRoute().params.slug
const { hydrateReady } = useHydrateHook()

await onBeforeAdyenData({ withRoutes: true, globalDataContentType: 'PageCareersCareerTypesDetail' })

const { data } = await useAsyncData('careers-vacancies-detail-data-' + slug + '-' + locale.value, async () => {
  const page = await useAsyncDataWithQuery(queryPageCareersCareerTypesDetail, {})

  return {
    pageData: page?.pageData
  }
})

await onAfterAdyenData(data.value)

const icon = computed(() => {
  const logo = getIconForCareerType(content.value?.careerType)
  return mapperIcon(logo)
})

const testimonialForIndex = (index) => content.value.testimonials?.items?.[index - 1] || {}

const appendLabelsToVideo = (video) => {
  if (!video) {
    return null
  }

  return {
    ariaLabelPlay: labels.play,
    ariaLabelClose: labels.close,
    ...video
  }
}
</script>
